import type { NavLink, TableBlockProps, TableRow } from '../types';
import { theme, sectionColors } from '../theme';
import { safeHttpHref } from './newsletterShared';
import { cellText, parseCell } from './tableCellLinks';

const CELL_KEYS = ['c1', 'c2', 'c3', 'c4'] as const;
export const TABLE_MAX_COLUMNS = CELL_KEYS.length;

// A light tint of the section's own text colour, so the stripe reads on any section background
// (a brand surface, a custom dark band) without a second colour to keep in step.
const ZEBRA = { backgroundColor: 'color-mix(in srgb, currentColor 6%, transparent)' } as const;

const clean = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** The header labels worth showing: trimmed, at most four. Trailing blank headings are dropped. */
export function tableColumns(columns: string[] | undefined): string[] {
  if (!Array.isArray(columns)) return [];
  const out = columns.slice(0, TABLE_MAX_COLUMNS).map(clean);
  while (out.length > 0 && !out[out.length - 1]) out.pop();
  return out;
}

/** Each row as exactly `count` cells (c1..cN); rows with every shown cell blank are dropped. Cells keep their link markers. */
export function tableRows(rows: TableRow[] | undefined, count: number): string[][] {
  if (!Array.isArray(rows) || count <= 0) return [];
  return rows
    .map((r) => CELL_KEYS.slice(0, count).map((k) => clean((r ?? {})[k])))
    .filter((cells) => cells.some((c) => cellText(c).trim()));
}

/** A cell's words, with its links (tableCellLinks.ts) as real links in the theme's link colour. */
function CellContent({ cell }: { cell: string }) {
  return (
    <>
      {parseCell(cell).map((p, i) =>
        p.href ? (
          <a
            key={i}
            href={p.href}
            {...(p.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="underline underline-offset-2"
            style={{ color: theme.link }}
          >
            {p.text}
          </a>
        ) : (
          p.text
        ),
      )}
    </>
  );
}

/** Sources with a label; the href is kept only when it is an http(s) address. */
function tableSources(sources: NavLink[] | undefined): { label: string; href: string | null }[] {
  if (!Array.isArray(sources)) return [];
  return sources
    .map((s) => ({ label: clean(s?.label), href: safeHttpHref(clean(s?.href)) }))
    .filter((s) => s.label);
}

/**
 * A simple data table (schools, fees, distances). A semantic <table> inside a wrapper that
 * scrolls sideways on its own, so a wide table never widens the page on a phone. Zebra rows,
 * theme colours and fonts. Renders nothing without at least one column and one row.
 */
export function TableBlock(props: TableBlockProps) {
  const { heading, subheading } = props;
  const columns = tableColumns(props.columns);
  const rows = tableRows(props.rows, columns.length);
  const note = clean(props.note);
  const sources = tableSources(props.sources);
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  if (columns.length === 0 || rows.length === 0) return null;

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }} data-block="table">
      <div className="mx-auto max-w-5xl">
        {heading ? (
          <h2 className="text-center text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
            {heading}
          </h2>
        ) : null}
        {subheading ? <p className="mt-2 text-center text-base opacity-75">{subheading}</p> : null}
        <div
          className={`${heading || subheading ? 'mt-8 ' : ''}w-full max-w-full overflow-x-auto rounded-lg border border-black/10`}
          role="region"
          aria-label={heading || 'Table'}
          tabIndex={0}
        >
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr style={{ backgroundColor: theme.primary, color: theme.onPrimary, fontFamily: theme.headingFont }}>
                {columns.map((c, i) => (
                  <th key={i} scope="col" className="min-w-[8rem] px-4 py-3 align-bottom font-semibold">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, r) => (
                <tr
                  key={r}
                  className="border-t border-black/10"
                  style={r % 2 === 1 ? ZEBRA : undefined}
                >
                  {cells.map((cell, c) =>
                    c === 0 ? (
                      <th key={c} scope="row" className="min-w-[8rem] px-4 py-3 align-top font-medium">
                        <CellContent cell={cell} />
                      </th>
                    ) : (
                      <td key={c} className="min-w-[8rem] px-4 py-3 align-top">
                        <CellContent cell={cell} />
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {note ? <p className="mt-3 text-xs leading-relaxed opacity-75">{note}</p> : null}
        {sources.length > 0 ? (
          <p className="mt-2 text-xs leading-relaxed opacity-75">
            <span className="font-semibold">Sources: </span>
            {sources.map((s, i) => (
              <span key={i}>
                {i > 0 ? ', ' : null}
                {s.href ? (
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="underline underline-offset-2"
                    style={{ color: theme.link }}
                  >
                    {s.label}
                  </a>
                ) : (
                  s.label
                )}
              </span>
            ))}
          </p>
        ) : null}
      </div>
    </section>
  );
}
